// 攪拌バッチの読取済QRエントリリスト。
// 1行=1消費行(ConstructionMaterialConsumption)。使用量を重量(kg)で編集でき、読取前残量(このバッチの他行を
// 除いた alive Σ から導出)を超える入力は保存せず、行内に警告を表示する。
// ※他の消費記録を書き換える「前回使用量の自動修正」は 2026-07-21 に廃止した(他データへの副作用を避けるため)。

import { useState, JSX } from "react";
import { Stack, Box, Typography, TextField, Chip, IconButton } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { ExtendedDB } from "../../../types/extendedDB";
import type { ConstructionMaterialConsumptionBase } from "../../../schema/generated/ConstructionMaterialConsumption";
import { FULL_EPS, getMaterialSetKg, formatRatioAsBagsThenKg, consumptionParentDeletedLabel } from "../../../util/consumption";

type CmcLike = { id: string; material_qr_id: number; used_ratio: number; deleted_at: number | null; construction_id: string; createdAt: number };

// QRの残割合(自行=excludeCmcIdを除く alive Σ から導出)。エントリ入力欄の上限判定に使う。
export function remainingRatioExcluding(cmc: CmcLike[], qrId: number, excludeCmcId: string): number {
  let total = 0;
  for (const row of cmc) {
    if (row.material_qr_id === qrId && row.deleted_at == null && row.id !== excludeCmcId) {
      total += row.used_ratio ?? 1;
    }
  }
  return Math.max(0, 1 - Math.min(1, total));
}

// 入力値を used_ratio へ変換する。セットkgが分かる場合は kg 入力、不明(0)なら袋(割合)入力として扱う。
export function inputToRatio(rawValue: string, setKg: number): number {
  const parsed = Number(rawValue);
  const value = Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
  if (setKg > 0) {
    return value / setKg;
  }
  return Math.min(1, value);
}

// used_ratio を入力欄の初期値文字列へ変換する(kg 入力なら小数1桁、袋入力なら小数2桁)。
export function ratioToInputValue(ratio: number, setKg: number): string {
  if (setKg > 0) {
    return String(Math.round(ratio * setKg * 10) / 10);
  }
  return String(Math.round(ratio * 100) / 100);
}

export function ConsumptionEntryList({
  db, entries, allCmc, busy, run, reload, setSnackbar, patchRatio, deleteEntry,
}: {
  db: ExtendedDB;
  entries: ConstructionMaterialConsumptionBase[];
  allCmc: ConstructionMaterialConsumptionBase[];
  busy: boolean;
  run: (fn: () => Promise<void>, errLabel: string) => void;
  reload: () => Promise<unknown> | void;
  setSnackbar: (s: string) => void;
  patchRatio: (row: ConstructionMaterialConsumptionBase, newRatio: number) => Promise<unknown>;
  deleteEntry: (id: string) => Promise<unknown>;
}) {
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [overWarnings, setOverWarnings] = useState<Record<string, string>>({});

  const clearLocal = (id: string) => {
    setDrafts((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setOverWarnings((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const setKgForQr = (qrId: number): number => {
    const qr = db.materialQrs.find((q) => q.id === qrId);
    const lot = qr?.lot_id != null ? db.lots.find((l) => l.id === qr.lot_id) : null;
    if (lot?.material_id == null) {
      return 0;
    }
    return getMaterialSetKg(db.materialDetails, lot.material_id);
  };

  const commitRatio = (row: ConstructionMaterialConsumptionBase, ratio: number) => run(async () => {
    await patchRatio(row, ratio);
    await reload();
    setSnackbar("使用量を保存しました");
  }, "使用量の保存に失敗しました");

  const onDeleteClick = (id: string) => run(async () => {
    await deleteEntry(id);
    await reload();
  }, "削除に失敗しました");

  // 残量超過の入力は保存しない(他の消費記録を書き換える自動修正は行わない)。
  // 入力値は残したまま行内に警告を出し、ユーザーに修正してもらう。
  const handleBlur = (row: ConstructionMaterialConsumptionBase, rawValue: string) => {
    const setKg = setKgForQr(row.material_qr_id);
    const ratio = inputToRatio(rawValue, setKg);
    if (Math.abs(ratio - row.used_ratio) < FULL_EPS) {
      clearLocal(row.id);
      return;
    }
    const remainBefore = remainingRatioExcluding(allCmc, row.material_qr_id, row.id);
    if (ratio <= remainBefore + FULL_EPS) {
      clearLocal(row.id);
      commitRatio(row, Math.min(1, ratio));
      return;
    }
    setDrafts((prev) => ({ ...prev, [row.id]: rawValue }));
    setOverWarnings((prev) => ({
      ...prev,
      [row.id]: `読取前残量 ${formatRatioAsBagsThenKg(remainBefore, setKg)} を超えているため保存していません。残量の範囲内で入力し直してください。`,
    }));
  };

  if (entries.length === 0) {
    return null;
  }

  return (
    <Stack gap={0.75} sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 1 }}>
      {entries.map((row) => {
        const qr = db.materialQrs.find((q) => q.id === row.material_qr_id);
        const lot = qr?.lot_id != null ? db.lots.find((l) => l.id === qr.lot_id) : null;
        const setKg = setKgForQr(row.material_qr_id);
        const remainBefore = remainingRatioExcluding(allCmc, row.material_qr_id, row.id);
        const draftValue = drafts[row.id] ?? ratioToInputValue(row.used_ratio, setKg);
        const draftRatio = inputToRatio(draftValue, setKg);
        const overWarning = overWarnings[row.id];
        const parentDeletedLabel = consumptionParentDeletedLabel(row, {
          constructions: db.constructions,
          stirBatches: db.stirBatches,
          processExecutions: db.processExecutions,
        });

        let chipNode: JSX.Element;
        if (parentDeletedLabel != null) {
          chipNode = <Chip size="small" color="error" label={parentDeletedLabel} sx={{ height: 22 }} />;
        } else if (row.used_ratio <= FULL_EPS) {
          chipNode = <Chip size="small" label="0kg" sx={{ height: 22 }} />;
        } else if (row.used_ratio >= 1 - FULL_EPS) {
          chipNode = <Chip size="small" color="success" icon={<CheckCircleIcon />} label="全量" sx={{ height: 22 }} />;
        } else {
          chipNode = <Chip size="small" color="warning" variant="outlined" label="半端" sx={{ height: 22 }} />;
        }

        return (
          <Box key={row.id}>
            <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
              <Typography variant="body2" fontWeight={600} sx={{ minWidth: 72 }}>QR#{row.material_qr_id}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 90 }}>
                {lot?.lot_no ?? (lot ? `LOT#${lot.id.slice(0, 8)}` : "-")}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 150 }}>
                読取前残量 {formatRatioAsBagsThenKg(remainBefore, setKg)}
              </Typography>
              <TextField
                size="small" type="number" label={setKg > 0 ? "使用量(kg)" : "使用量(袋)"} disabled={busy}
                error={overWarning != null}
                value={draftValue}
                inputProps={setKg > 0 ? { step: 0.1, min: 0 } : { step: 0.01, min: 0, max: 1 }}
                sx={{ width: 130 }}
                onChange={(e) => setDrafts((prev) => ({ ...prev, [row.id]: e.target.value }))}
                onBlur={(e) => handleBlur(row, e.target.value)}
              />
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 70 }}>
                = {formatRatioAsBagsThenKg(Math.min(1, draftRatio), 0)}
              </Typography>
              {chipNode}
              <Box flex={1} />
              <IconButton size="small" disabled={busy} onClick={() => onDeleteClick(row.id)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
            {overWarning != null && (
              <Typography variant="caption" sx={{ color: "error.main", pl: 1, mt: 0.25 }} display="block">
                {overWarning}
              </Typography>
            )}
          </Box>
        );
      })}
    </Stack>
  );
}
